import { gsap } from 'gsap';

import { Reel } from '../../core/classes/reel';
import { isMobile } from '../../core/helpers/isMobile';
import { isoStandard } from '../../core/helpers/isoStandard';
import { initGameLoopManager } from '../../core/helpers/gameLoop';
import { GAME_SPEED_TYPES } from '../../core/constants/gameConstants';
import { addTouchControl } from '../../core/classes/touchSpinControll';
import { amountContainerInfo } from '../../core/constants/amountContainerInfo';
import { SpinIconNumberProvider } from '../../core/helpers/spinIconNumberProvider';
import { handleSymbolAnimationPositionInMatrix } from '../../core/helpers/createWinLinesMatrix';
import {
  iconStates,
  iconsIdByName,
  randomIconsGroup,
  slotBarSymbolNames,
} from '../../core/constants/symbolsNames';

import { gameStore } from '../../store/gameStore';
import { winBarStore } from '../../store/winBarStore';
import { gambleStore } from '../../store/gambleStore';
import { freeBetStore } from '../../store/freeBetStore';
import { userInfoStore } from '../../store/userInfoStore';
import { bettingPartStore } from '../../store/bettingPartStore';

import { gameService } from '../gameService';
import { winPopupShownMinOdd } from '../../gameConstants';
import { SoundTypes, soundService } from '../soundService';
import { gameResourcesService } from '../gameResourcesService';

class GameDrawer {
  columns = [];

  iconsHeight = isMobile() ? 100 : 196;

  gameSpeed = 300;

  spinTimeOudId = [];

  isForceStop = false;

  isResponseEnded = false;

  columnsStopCount = 0;

  spinDirection = 1;

  expandEndedColumns = [];

  columnsMoveSpeed = isMobile() ? 28 : 50;

  randomSymbolGroupId = 0;

  startLoopCount = 7;

  calculateReelParams() {
    const rows = 3;
    const iconsDistance = 0;
    const iconsInReel = 3;
    const reverseLineOffset = isMobile() ? 30 : 45;
    const { iconsHeight } = this;
    const finalPositions = isMobile() ? [0, 100, 200] : [0, 196, 392];
    const topLine = Math.floor(finalPositions[0] - iconsHeight / 2);
    const webColumnPositions = [100, 300, 500, 700, 900];
    const mobileColumnPositions = [0, 104, 208, 312, 416];
    const bottomLine = rows * iconsHeight + (rows - 1) * iconsDistance;
    const { columnsMoveSpeed } = this;

    return {
      iconsInReel,
      iconsHeight,
      iconsDistance,
      topLine,
      webColumnPositions,
      mobileColumnPositions,
      bottomLine,
      takeOffLineOffset: 50,
      reverseLineOffset,
      columnsMoveSpeed,
      iconMargins: { default: (iconsHeight + iconsDistance) / 2 },
      finalPositions,
    };
  }

  initGameReels(spinContainer, winContainer, ticker) {
    this.columns = [];
    const columnsCount = 5;
    initGameLoopManager(ticker);
    const reelParams = this.calculateReelParams();
    for (let columnNumber = columnsCount - 1; columnNumber >= 0; columnNumber -= 1) {
      const reel = new Reel(
        columnNumber,
        spinContainer,
        winContainer,
        reelParams,
        new SpinIconNumberProvider(columnNumber),
        columnNumber === columnsCount - 1
      );
      this.columns[columnNumber] = reel;
    }
    this.columns.forEach((col, index) => {
      col.fullUpdateFromResponseData(gameStore.gameInitialMatrix[index]);
    });
    gameResourcesService.setupPixiPlugins();
  }

  generateRandomSymbolType() {
    this.randomSymbolGroupId = Math.floor(Math.random() * randomIconsGroup.length);
  }

  startSpinAnimation() {
    this.generateRandomSymbolType();
    gameResourcesService.resetAllLineSpine();
    gameResourcesService.resetBoardSpine();
    this.resetReelState();
    this.updateReelAnimationSpeed();
    this.columns.forEach((col) => {
      col.resetIcons();
      col.startLoopCount = this.startLoopCount;
      col.columnsMoveSpeed = this.columnsMoveSpeed;
      col.startSpin(this.spinDirection);
    });
    this.spinDirection = 1;
  }

  resetReelState() {
    this.startedColumns = 5;
    this.expandEndedColumns = [];
    this.columnsStopCount = 0;
  }

  handleExpandingStarsArray(expandingStars) {
    const expandStarsIndexArr = expandingStars.reduce((acc, curr, index) => {
      if (curr) {
        acc.push(index);
      }
      return acc;
    }, []);
    return expandStarsIndexArr;
  }

  startReSpinTextAnimation() {
    soundService.play(SoundTypes.ReSpinTextSound);
    gameResourcesService.reSpinTextScene.play('animation');
    gameResourcesService.reSpinTextScene.addListener(
      'complete',
      () => {
        gameResourcesService.reSpinTextScene.stop();
      },
      null,
      'animation'
    );
  }

  startReSpinAnimation(expandingStars) {
    this.startReSpinTextAnimation();
    const expandStarsArr = this.handleExpandingStarsArray(expandingStars);
    gameResourcesService.resetAllLineSpine();
    gameResourcesService.resetBoardSpine();
    this.columnsStopCount = 0;
    const columns = this.columns.filter((el) => !expandStarsArr.includes(el.index));
    const wildColumns = this.columns.filter((el) => expandStarsArr.includes(el.index));
    this.startedColumns = columns.length;
    soundService.play(SoundTypes.ReSpin);
    columns.forEach((col) => {
      col.columnsMoveSpeed = isMobile() ? 60 : 110;
      col.resetIcons();
      col.startReSpinIconsState();
      col.startSpin();
    });
    wildColumns.forEach((column) => {
      column.resetIcons();
    });
  }

  stopSpinAnimation() {
    const reelDelay = this.isForceStop ? 0 : this.gameSpeed;
    this.isResponseEnded = true;
    this.columns.forEach((col, index) => {
      const timeOutId = setTimeout(() => {
        col.startLoopCount = this.startLoopCount;
        col.stopSpin(this.isForceStop);
      }, index * reelDelay);
      this.spinTimeOudId.push(timeOutId);
    });
  }

  stopReSpinAnimation() {
    this.isResponseEnded = true;
    this.columns.forEach((col) => {
      col.stopSpin(false);
    });
    gameResourcesService.boardsSpine.forEach((board) => {
      board.play('shaking');
    });
  }

  updateReelAnimationSpeed() {
    if (bettingPartStore.gameSpeedType === GAME_SPEED_TYPES.turbo) {
      this.gameSpeed = 0;
      this.columnsMoveSpeed = isMobile() ? 40 : 80;
      this.startLoopCount = 20;
    } else if (bettingPartStore.gameSpeedType === GAME_SPEED_TYPES.fast) {
      this.gameSpeed = 100;
      this.startLoopCount = 10;
      this.columnsMoveSpeed = isMobile() ? 32 : 55;
    } else {
      this.gameSpeed = 300;
      this.startLoopCount = 7;
      this.columnsMoveSpeed = isMobile() ? 28 : 50;
    }
  }

  forceStopAnimation() {
    this.isForceStop = true;
    this.startLoopCount = 20;
    if (this.isResponseEnded) {
      this.spinTimeOudId.forEach((timeOudId) => {
        clearTimeout(timeOudId);
      });
      this.columns.forEach((column) => {
        column.startLoopCount = 20;
        column.stopSpin(true);
      });
    }
  }

  handleReelBoardAnimation(reelIndex) {
    const {
      spinResult: { additional = {} },
    } = gameStore;
    const { expandingStars = [] } = additional;
    this.columns[reelIndex]._reelIcons.forEach((icon) => {
      if (
        icon.iconNumber === iconsIdByName[slotBarSymbolNames.icon9] &&
        expandingStars[reelIndex]
      ) {
        gameResourcesService.boardsSpine[reelIndex].play('shaking');
      }
    });
  }

  handleReelSound(reelIndex) {
    if (gameStore.isReSpin) {
      soundService.play(SoundTypes.ReSpinStop);
    } else {
      soundService.play(SoundTypes.ReelStop);
    }
    this.columns[reelIndex]._reelIcons.forEach((icon) => {
      if (icon.iconNumber === iconsIdByName[slotBarSymbolNames.icon9]) {
        soundService.play(SoundTypes.WildSymbol);
      }
    });
  }

  async handleStopReelAnimation() {
    this.columnsStopCount += 1;
    if (this.columnsStopCount === this.startedColumns) {
      this.isForceStop = false;
      this.isResponseEnded = false;
      const {
        spinResult: { additional = {}, winAmount, gambleInfo, freeBetInfo, tournamentInfo },
      } = gameStore;
      const { wins, expandingStars, retrigger } = additional;

      await this.startWinAnimation({
        winAmount,
        gambleInfo,
        wins,
        retrigger,
        expandingStars,
        freeBetInfo,
        tournamentInfo,
      });
    }
  }

  playWinCoinAnimation() {
    gameResourcesService.winCoinSpine.play('animation');
    gameResourcesService.winCoinSpine.addListener(
      'complete',
      () => {
        gameResourcesService.winCoinSpine.stop();
      },
      null,
      'animation'
    );
  }

  skipWinAnimation() {
    this.winAnimationTimeLine && this.winAnimationTimeLine.progress(1);
  }

  animateWinAmount({ value, duration = 0.8, isReset = false }) {
    if (freeBetStore.currentFreeBet && !value) {
      return;
    }
    const { winAmountTexture } = gameResourcesService;
    if (isReset) {
      gameResourcesService.winAmountTexture.text = '';
      gameResourcesService.winAmountTexture.textContent = '';
    }
    this.winAnimationTimeLine = gsap.timeline();
    this.winAnimationTimeLine.to(winAmountTexture, {
      textContent: value,
      duration,
      ease: 'none',
      snap: { textContent: 0.01 },
      stagger: {
        each: 1.0,
        onUpdate: function () {
          winAmountTexture.text = isoStandard(+this.targets()[0].textContent, true);
        },
      },
      onComplete: function () {
        winAmountTexture.text = isoStandard(+value, true);
      },
    });
  }

  async startWinAnimation({
    winAmount,
    gambleInfo,
    freeBetInfo,
    wins = [],
    retrigger = false,
    expandingStars = [0, 0, 0, 0, 0],
  }) {
    if (expandingStars.some((el) => el)) {
      await this.startWildAnimation(expandingStars);
    }
    let winLinesData = [];

    if (winAmount) {
      this.animateWinAmount({ value: winAmount, isReset: !gameStore.isReSpin });
      winBarStore.setWinAmount(winAmount);
      winBarStore.updateShowAmountContainerInfo(amountContainerInfo.winContainer, true);
      !retrigger && this.playWinCoinAnimation();
    } else if (gameStore.isReSpin && winAmount !== winBarStore.winAmount) {
      this.animateWinAmount({ value: winAmount, isReset: !winBarStore.winAmount });
      winBarStore.setWinAmount(winAmount);
    }
    if (freeBetInfo && !freeBetInfo.reminingSpinCount) {
      winBarStore.setTotalWinAmount(freeBetInfo.totalWin);
      freeBetStore.setCurrentFreeBet({
        ...freeBetStore.currentFreeBet,
        freeBetInfo,
        remainingCount: freeBetInfo.reminingSpinCount,
      });
    }
    gambleStore.setGambleInfo(gambleInfo);
    if (!retrigger) gameStore.setIsSpinStart(false);
    if (wins.length) {
      winLinesData = handleSymbolAnimationPositionInMatrix(wins);
      await this.startAllWinLineAnimation(winLinesData);
    }

    gameResourcesService.resetAllLineSpine();
    gameStore.setIsReSpin(false);
    if (retrigger) {
      gameService.doReSpin(expandingStars);
    } else {
      gameService.spinFinished({
        winAmount,
        gambleInfo,
        odd: gameStore.odd,
      });
      if (!bettingPartStore.selectedAutoBetCount && winLinesData.length) {
        await this.startWinLineAnimation(winLinesData, 0, gameStore.odd);
      }
    }
  }

  startWinLineAnimation(winLinesData, start) {
    return new Promise((resolve) => {
      const winLine = winLinesData[start];
      const { line, lines } = winLine;
      const lineIndex = line - 1;
      const isFromRight = line > 20;
      const animationName = isFromRight ? 'animation2' : 'animation';
      this.columns.forEach((column, i) => {
        column._reelIcons.forEach((icon, j) => {
          if (lines[j][i] === 1) {
            icon.changeState(iconStates.win);
          } else {
            icon.iconNumber === 9
              ? icon.changeState(iconStates.idle)
              : icon.changeState(iconStates.noWin);
          }
          if (lines[j][i]) {
            gameResourcesService.allWinLinesBone[lineIndex][isFromRight ? 4 - i : i].y =
              -icon.y + (isMobile() ? 100 : 200);
          }
        });
      });
      winBarStore.setWinLine(line);
      if (gameStore.odd <= winPopupShownMinOdd) {
        soundService.play(SoundTypes.WinOneLineSound);
      }
      gameResourcesService.allLinesSpine[lineIndex].play(animationName);
      gameResourcesService.allLinesSpine[lineIndex].addListener(
        'complete',
        () => {
          start += 1;
          start = start > winLinesData.length - 1 ? 0 : start;
          this.startWinLineAnimation(winLinesData, start);
          resolve();
        },
        null,
        animationName
      );
    });
  }

  startAllWinLineAnimation(winLinesData) {
    return new Promise((resolve) => {
      let resolverCount = 0;
      soundService.play(SoundTypes.WinningLineSound);
      winLinesData.forEach(({ line, allLines, lines }) => {
        const lineIndex = line - 1;
        const isFromRight = line > 20;
        const animationName = isFromRight ? 'animation2' : 'animation';
        this.columns.forEach((column, i) => {
          column._reelIcons.forEach((icon, j) => {
            if (allLines[j][i] === 1) {
              icon.changeState(iconStates.win);
            } else {
              icon.iconNumber === 9
                ? icon.changeState(iconStates.idle)
                : icon.changeState(iconStates.noWin);
            }
            if (lines[j][i]) {
              gameResourcesService.allWinLinesBone[lineIndex][isFromRight ? 4 - i : i].y =
                -icon.y + (isMobile() ? 100 : 200);
            }
          });
        });
        gameResourcesService.allLinesSpine[lineIndex].play(animationName, true);
        gameResourcesService.allLinesSpine[lineIndex].addListener(
          'complete',
          () => {
            resolverCount += 1;
            if (resolverCount === winLinesData.length) {
              resolve();
            }
          },
          null,
          animationName
        );
      });
    });
  }

  startWildAnimation(expandingStars) {
    return new Promise((resolve) => {
      const expandStarsArr = this.handleExpandingStarsArray(expandingStars);
      const isResolve = expandStarsArr.every((el) => this.expandEndedColumns.includes(el));
      if (isResolve) {
        resolve();
        return;
      }
      this.columns.forEach((reel) => {
        if (expandStarsArr.includes(reel.index) && !this.expandEndedColumns.includes(reel.index)) {
          reel._reelIcons.forEach((symbol) => {
            symbol.changeIcon(9, iconStates.winExpand);
            gameResourcesService.boardsSpine[reel.index].play('winWild2');
            soundService.play(SoundTypes.WildOpeningSound);
            symbol.icon.addListener(
              'complete',
              () => {
                symbol.icon.play('idle', true);
              },
              null,
              iconStates.winExpand
            );
            gameResourcesService.boardsSpine[reel.index].addListener(
              'complete',
              () => {
                symbol.icon.play('idle', true);
                this.expandEndedColumns.push(reel.index);
                gameResourcesService.boardsSpine[reel.index].play('idle', true);
                resolve();
              },
              null,
              'winWild2'
            );
          });
        }
      });
    });
  }

  handleJackpotWin() {
    let resolverCount = 0;
    return new Promise((resolve) => {
      this.columns.forEach((reel) => {
        reel._reelIcons.forEach((slotIcon) => {
          if (slotIcon.iconNumber === 400) {
            slotIcon.icon.play('win', true);
            setTimeout(() => {
              slotIcon.icon.addListener(
                'complete',
                () => {
                  resolverCount += 1;
                  if (resolverCount === 3) {
                    resolve(resolverCount);
                  }
                },
                null,
                'win'
              );
            }, 500);
          }
        });
      });
    });
  }

  updateColumnsDirection(spinDirection) {
    this.spinDirection = spinDirection;
  }

  execute(spinContainer, winContainer, ticker) {
    this.initGameReels(spinContainer, winContainer, ticker);
    if (!userInfoStore.isReplay) {
      addTouchControl(spinContainer, winContainer, this.columns);
    }
  }
}

export const gameDrawer = new GameDrawer();
